import axios from "axios";
import { SQL_API_URL } from "./config";

const client = axios.create({
  baseURL: SQL_API_URL,
  timeout: 630000,
});

function sessionHeaders() {
  const sessionId = sessionStorage.getItem("sqlSessionId");
  return sessionId ? { "X-SQL-Session": sessionId } : {};
}

export function clearSqlSession() {
  sessionStorage.removeItem("sqlSessionId");
  sessionStorage.removeItem("sqlConnection");
}

client.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      clearSqlSession();
    }
    return Promise.reject(err);
  }
);

export async function sqlHealth() {
  const res = await client.get("/api/sql/health");
  return res.data;
}

export async function sqlConnect(payload) {
  const res = await client.post("/api/sql/connect", payload);
  if (res.data?.sessionId) {
    sessionStorage.setItem("sqlSessionId", res.data.sessionId);
    sessionStorage.setItem("sqlConnection", JSON.stringify(res.data.connection || {}));
  }
  return res.data;
}

export async function sqlDisconnect() {
  try {
    await client.post("/api/sql/disconnect", {}, { headers: sessionHeaders() });
  } finally {
    clearSqlSession();
  }
}

export async function sqlDatabases() {
  const res = await client.get("/api/sql/databases", { headers: sessionHeaders() });
  return res.data;
}

export async function sqlUseDatabase(database) {
  const res = await client.post(
    "/api/sql/use-database",
    { database },
    { headers: sessionHeaders() }
  );
  if (res.data?.connection) {
    sessionStorage.setItem("sqlConnection", JSON.stringify(res.data.connection));
  }
  return res.data;
}

export async function sqlTables() {
  const res = await client.get("/api/sql/tables", { headers: sessionHeaders() });
  return res.data;
}

export async function sqlColumns(schema, table) {
  const res = await client.get("/api/sql/columns", {
    headers: sessionHeaders(),
    params: { schema, table },
  });
  return res.data;
}

export async function sqlRelatedTables(schema, table) {
  const res = await client.get("/api/sql/related-tables", {
    headers: sessionHeaders(),
    params: { schema, table },
  });
  return res.data;
}

export async function sqlQuery(sql, maxRows = 0, options = {}) {
  const unlimited = !maxRows || maxRows <= 0;
  const res = await client.post(
    "/api/sql/query",
    { sql, maxRows: unlimited ? 0 : maxRows, unlimited },
    { headers: sessionHeaders(), signal: options.signal }
  );
  return res.data;
}

export async function sqlCancel() {
  const res = await client.post(
    "/api/sql/cancel",
    {},
    { headers: sessionHeaders() }
  );
  return res.data;
}

export function isSqlQueryCanceledError(err) {
  return (
    err?.code === "ERR_CANCELED" ||
    err?.name === "CanceledError" ||
    err?.name === "AbortError" ||
    axios.isCancel?.(err) ||
    err?.response?.status === 499 ||
    err?.response?.data?.cancelled === true
  );
}

export function getSavedSqlConnection() {
  try {
    const raw = JSON.parse(sessionStorage.getItem("sqlConnection") || "null");
    if (!raw || typeof raw !== "object") return null;
    if (raw.engine != null && String(raw.engine).trim() !== "") {
      const engine =
        String(raw.engine).toLowerCase() === "mysql" ? "mysql" : "mssql";
      return { ...raw, engine };
    }
    // Prefer saved login engine over forcing mssql when meta.engine is missing
    let fallbackEngine = null;
    try {
      const login = JSON.parse(sessionStorage.getItem("sqlEasyLogin") || "null");
      if (login?.engine) fallbackEngine = login.engine;
    } catch {
      /* ignore */
    }
    if (fallbackEngine) {
      const engine =
        String(fallbackEngine).toLowerCase() === "mysql" ? "mysql" : "mssql";
      return { ...raw, engine };
    }
    return { ...raw };
  } catch {
    return null;
  }
}

export function hasSqlSession() {
  return Boolean(sessionStorage.getItem("sqlSessionId"));
}

/** True when the bridge rejected the session. */
export function isSqlSessionExpiredError(err) {
  return err?.response?.status === 401;
}
